/* istanbul ignore file */
import { useState } from 'react'
import logger from '@adc/logger'
import * as blueprint from '@adc/blueprint'
import _ from 'lodash'
import {
  AddressSuggestionField,
  Text,
  constants,
  Button,
  SelectedAddress,
} from '@adc/ui-adc'
import configment from '@adc/configment'
import classNames from 'classnames/bind'
import { DynamicFieldProps, getDefaultValue } from '../../dynamicField'
// @ts-ignore next
import landingHeroVideo from '../../../../assets/address_test1.mp4'
import { useStepManagerContext } from '../stepManager'
import styles from './styles.scss'

const { ColorNames } = constants

const cx = classNames.bind(styles)

interface LocationSuggestionsResponse {
  rawLocationSuggestions: Array<{
    value?: string
    county?: string
    city?: string
    state?: string
    zip?: string
    type?: string
    url?: string
    coordinates?: {
      lat: number
      lon: number
    }
  }>
}

const Address = ({ question, error, onChange }: DynamicFieldProps) => {
  const { field } = question
  const { data, actions } = useStepManagerContext()
  const { stepProgression, leadIntake } = data
  const { onNextStep } = actions

  const [fieldValue, setFieldValue] = useState(
    leadIntake[field] || getDefaultValue(question, leadIntake)
  )

  const lookupLocation = async (search: string): Promise<string | null> => {
    try {
      const result = (await blueprint.search.providers.fetchLocationSuggestions(
        search
      )) as LocationSuggestionsResponse | null

      if (!result?.rawLocationSuggestions?.[0]?.county) {
        return null
      }
      return result.rawLocationSuggestions[0].county
    } catch (error) {
      logger.error('Error looking up county:', error)
      return null
    }
  }

  const onAddressSuggestChange = (event: SelectedAddress) => {
    const addressSuggestion = _.trim(event.selectedAddressText)
    setFieldValue(addressSuggestion)

    const extractedValues = _.pick(event, [
      'municipality',
      'postal_code',
      'country_primary_subdivision',
      'country_secondary_subdivision',
      'coordinates',
      'street_name',
      'street_number',
      'street_unit',
    ])
    const county =
      extractedValues.country_secondary_subdivision?.trim() || undefined

    const nextAddressValue = {
      'property.address': extractedValues.street_name,
      'property.street_number': extractedValues.street_number,
      'property.city': extractedValues.municipality,
      'property.county': county,
      'property.state': extractedValues.country_primary_subdivision,
      'property.postal_code': extractedValues.postal_code,
      'property.street_unit': extractedValues.street_unit,
      '__computed__.property.coordinates': {
        lat: extractedValues.coordinates?.lat,
        lng: extractedValues.coordinates?.lon,
      },
      '__computed__.property.addressDisplayText': addressSuggestion,
      '__computed__.property.is_option_selected': event.is_option_selected,
    }
    onChange(nextAddressValue)

    if (!county && event.is_option_selected) {
      const cityState = `${extractedValues.municipality}, ${extractedValues.country_primary_subdivision}`
      lookupLocation(cityState)
        .then(lookupResult => {
          if (lookupResult) {
            const updatedAddressValue = {
              ...nextAddressValue,
              'property.county': lookupResult,
            }
            onChange(updatedAddressValue)
          }
        })
        .catch(error => {
          logger.error('Error looking up county:', error)
        })
    }
  }

  return (
    <section className={cx('address-section-container')}>
      <div className={cx('address-section', 'landing-section')}>
        <div className={cx('address-section-content')}>
          <div>
            <div className={cx('address-section-title')}>
              Real Offers.
              <br />
              Right in your hands.
            </div>
            <Text size='big' color={ColorNames.White}>
              Auction.com is for serious sellers looking for the best offers in
              days instead of weeks.
            </Text>
            <div className={cx('address-box')}>
              <Text size='tiny'>
                Enter your property address to get started
              </Text>
              <div className={cx('address-field-container')}>
                <AddressSuggestionField
                  name='address_suggestion'
                  elmId='property_address_suggestion_field'
                  className={cx('address-suggestion-field')}
                  value={{
                    selectedAddressText: fieldValue,
                  }}
                  onChange={onAddressSuggestChange}
                  googleApiConfig={{
                    channelName: configment.get('GOOGLE_MAPS_CHANNEL_NAME', ''),
                    apiKey: configment.get('GOOGLE_MAPS_API_KEY', ''),
                  }}
                  displayFormattedAddress
                  googleAutocompleteOptions={{
                    componentRestrictions: { country: ['us', 'pr'] },
                  }}
                  touched
                  error={!stepProgression.isCurrentlyValidating && error}
                />
              </div>
              <Button
                isStretch
                role='primary'
                disabled={stepProgression.isNextDisabled}
                onClick={onNextStep}
                loading={stepProgression.isCurrentlyValidating}
              >
                Start My Property Listing
              </Button>
            </div>
            <div className={cx('statistic-container')}>
              <div>
                <Text
                  weight='heavy'
                  size='massive'
                  color={ColorNames.AdcLightBlue}
                >
                  50,000+
                </Text>
                <Text color={ColorNames.White}>Properties Sold</Text>
              </div>
              <div>
                <Text
                  weight='heavy'
                  size='massive'
                  color={ColorNames.AdcLightGreen}
                >
                  98%
                </Text>
                <Text color={ColorNames.White}>Seller Satisfaction</Text>
              </div>
            </div>
          </div>
        </div>
        <div className={cx('video-background')}>
          <video autoPlay muted loop className={cx('video')}>
            <source src={landingHeroVideo} type='video/mp4' />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </section>
  )
}
export default Address
